/**
 * UK Direct — Online Store 2.0 Theme Interactive Scripts
 * Handles Shopify Storefront APIs, AJAX Cart Operations, and Drawer Management.
 */

(function() {
  'use strict';

  const formatMoney = (cents) => {
    return '£' + (cents / 100).toFixed(2);
  };

  const getRoute = (path) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    return root + (path.startsWith('/') ? path.substring(1) : path);
  };

  class UKDirectTheme {
    constructor() {
      this.initDrawer();
      this.initMobileMenu();
      this.initKlarnaModal();
      this.initPredictiveSearch();
      this.initVariantSelectors();
      this.initQuantitySelectors();
      this.initAjaxCart();
      this.initThumbnails();
      this.initCartEvents();
    }

    initDrawer() {
      const openTriggers = document.querySelectorAll('[data-action="open-cart"], .js-open-cart');
      const closeTriggers = document.querySelectorAll('.js-close-cart, [data-action="close-cart"], .drawer-overlay');
      const drawer = document.getElementById('shopify-cart-drawer');
      const overlay = document.getElementById('shopify-drawer-overlay');

      if (!drawer || !overlay) return;

      openTriggers.forEach(btn => {
        btn.addEventListener('click', (e) => {
          if (btn.tagName === 'A') e.preventDefault();
          drawer.classList.add('is-open');
          overlay.classList.add('is-open');
          document.body.style.overflow = 'hidden';
          this.refreshCartDrawer();
        });
      });

      closeTriggers.forEach(btn => {
        btn.addEventListener('click', () => {
          drawer.classList.remove('is-open');
          overlay.classList.remove('is-open');
          document.body.style.overflow = '';
        });
      });
    }

    initMobileMenu() {
      const openTriggers = document.querySelectorAll('.js-open-mobile-menu');
      const closeTriggers = document.querySelectorAll('.js-close-mobile-menu');
      const menu = document.getElementById('shopify-mobile-menu');
      const overlay = document.getElementById('shopify-mobile-overlay');

      if (!menu || !overlay) return;

      openTriggers.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          menu.style.left = '0';
          overlay.style.opacity = '1';
          overlay.style.visibility = 'visible';
          document.body.style.overflow = 'hidden';
        });
      });

      closeTriggers.forEach(btn => {
        btn.addEventListener('click', () => {
          menu.style.left = '-100%';
          overlay.style.opacity = '0';
          overlay.style.visibility = 'hidden';
          document.body.style.overflow = '';
        });
      });
    }

    async refreshCartDrawer() {
      try {
        const response = await fetch(getRoute('cart.js'));
        if (!response.ok) return;
        const cart = await response.json();
        
        // Fetch Section API for updated Drawer HTML
        const sectionRes = await fetch(getRoute('?section_id=cart-drawer'));
        if (sectionRes.ok) {
          const sectionText = await sectionRes.text();
          const temp = document.createElement('div');
          temp.innerHTML = sectionText;
          const newDrawer = temp.querySelector('#shopify-cart-drawer');
          const currentDrawer = document.getElementById('shopify-cart-drawer');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
          }
        }
        
        const countBadges = document.querySelectorAll('.js-cart-count');
        countBadges.forEach(badge => badge.textContent = cart.item_count);
        
        // Auto-refresh main cart page if active
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        }
      } catch (err) {
        // Silently fail if cart API is unavailable
      }
    }

    initCartEvents() {
      document.body.addEventListener('click', async (e) => {
        const btn = e.target.closest('.js-cart-qty-btn');
        const removeBtn = e.target.closest('.js-cart-remove-btn');
        
        if (removeBtn) {
          e.preventDefault();
          this.updateCartItem(removeBtn.getAttribute('data-key'), 0);
          return;
        }
        
        if (btn) {
          e.preventDefault();
          const key = btn.getAttribute('data-key');
          const input = btn.parentElement.querySelector(`.js-cart-qty-input`);
          if (!input) return;
          
          let currentQty = parseInt(input.value);
          let newQty = btn.getAttribute('data-action') === 'plus' ? currentQty + 1 : currentQty - 1;
          if (newQty < 0) newQty = 0;
          
          this.updateCartItem(key, newQty);
        }
      });
      
      document.body.addEventListener('change', (e) => {
        if (e.target.classList.contains('js-cart-qty-input')) {
          const key = e.target.getAttribute('data-key');
          const newQty = parseInt(e.target.value);
          if (newQty >= 0) {
            this.updateCartItem(key, newQty);
          }
        }
      });
    }

    async updateCartItem(key, quantity) {
      const drawer = document.getElementById('shopify-cart-drawer');
      if (drawer) drawer.style.opacity = '0.5';
      
      try {
        await fetch(getRoute('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        await this.refreshCartDrawer();
      } catch (err) {
        // Silent catch for cart update failure
      } finally {
        if (drawer) drawer.style.opacity = '1';
      }
    }

    initKlarnaModal() {
      const modal = document.getElementById('shopify-klarna-modal');
      const triggers = document.querySelectorAll('.js-open-klarna, .klarna-notice');
      const closeBtn = document.querySelectorAll('.js-close-klarna');

      if (!modal) return;

      triggers.forEach(el => {
        el.style.cursor = 'pointer';
        el.addEventListener('click', (e) => {
          e.preventDefault();
          modal.style.display = 'flex';
        });
      });

      closeBtn.forEach(btn => {
        btn.addEventListener('click', () => modal.style.display = 'none');
      });
    }

    initPredictiveSearch() {
      const input = document.querySelector('.js-predictive-input');
      const resultsContainer = document.querySelector('.js-predictive-results');

      if (!input || !resultsContainer) return;

      input.addEventListener('input', debounce(async () => {
        const query = input.value.trim();
        if (query.length < 2) {
          resultsContainer.innerHTML = '';
          return;
        }
        try {
          const res = await fetch(getRoute(`search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product`));
          const data = await res.json();
          const products = data.resources.results.products || [];
          resultsContainer.innerHTML = products.map(p => `
            <a href="${p.url}" class="search-item" style="display:flex; gap:12px; align-items:center; text-decoration:none; color:var(--color-primary); padding:8px 0; border-bottom:1px solid var(--color-border);">
              <img src="${p.image}" alt="${p.title}" style="width:40px; height:40px; object-fit:cover; border-radius:4px;">
              <span>${p.title} - ${formatMoney(p.price)}</span>
            </a>
          `).join('');
        } catch (err) {
          resultsContainer.innerHTML = `<div class="search-fallback">Search suggestions active upon Shopify CLI deployment.</div>`;
        }
      }, 300));
    }

    initVariantSelectors() {
      const jsonEl = document.querySelector('.js-product-json');
      if (!jsonEl) return;
      
      const variantsData = JSON.parse(jsonEl.textContent);
      const radioInputs = document.querySelectorAll('.js-variant-selector input[type="radio"]');
      const masterSelect = document.querySelector('.js-master-select');
      
      radioInputs.forEach(radio => {
        radio.addEventListener('change', () => {
          // Highlight selected label visually
          const fieldset = radio.closest('fieldset');
          fieldset.querySelectorAll('label').forEach(lbl => {
            lbl.style.borderWidth = '1px';
            lbl.style.borderColor = 'var(--color-border)';
            lbl.style.fontWeight = '400';
          });
          radio.parentElement.style.borderWidth = '2px';
          radio.parentElement.style.borderColor = 'var(--color-accent)';
          radio.parentElement.style.fontWeight = '600';
          
          // Update visual label value text
          const labelSpan = fieldset.querySelector('.js-variant-label-value');
          if (labelSpan) labelSpan.textContent = radio.value;

          // Find current combination
          const selectedOptions = Array.from(document.querySelectorAll('.js-variant-selector'))
            .map(fieldset => fieldset.querySelector('input[type="radio"]:checked')?.value);
            
          const matchedVariant = variantsData.find(v => {
            return selectedOptions.every((val, index) => v.options[index] === val);
          });

          if (matchedVariant) {
            // Update Master Select
            if (masterSelect) masterSelect.value = matchedVariant.id;
            
            // Update URL
            const url = new URL(window.location);
            url.searchParams.set('variant', matchedVariant.id);
            window.history.replaceState({path: url.href}, '', url.href);
            
            // Update Prices
            const priceEl = document.querySelector('.js-product-price');
            const comparePriceEl = document.querySelector('.js-product-compare-price');
            const mobilePriceEl = document.querySelector('.js-mobile-sticky-price');
            
            if (priceEl) priceEl.textContent = formatMoney(matchedVariant.price);
            if (mobilePriceEl) mobilePriceEl.textContent = formatMoney(matchedVariant.price);
            
            if (comparePriceEl) {
              if (matchedVariant.compare_at_price > matchedVariant.price) {
                comparePriceEl.textContent = formatMoney(matchedVariant.compare_at_price);
                comparePriceEl.style.display = 'inline';
                priceEl.style.color = 'var(--color-accent)';
              } else {
                comparePriceEl.style.display = 'none';
                priceEl.style.color = 'var(--color-primary)';
              }
            }

            // Update Stock Badge & Button
            const stockBadge = document.querySelector('.js-stock-badge');
            const addBtn = document.querySelector('.js-add-to-cart');
            const addBtnText = document.querySelector('.js-add-to-cart-text');

            if (matchedVariant.available) {
              if (stockBadge) {
                stockBadge.innerHTML = '&#10003; In Stock in GB';
                stockBadge.className = 'stock-badge js-stock-badge stock-badge--in';
                stockBadge.style.background = '#E8F5E9';
                stockBadge.style.color = '#2E7D32';
              }
              if (addBtn) addBtn.disabled = false;
              if (addBtnText) addBtnText.textContent = 'Add to Basket';
            } else {
              if (stockBadge) {
                stockBadge.innerHTML = 'Out of Stock';
                stockBadge.className = 'stock-badge js-stock-badge stock-badge--out';
                stockBadge.style.background = '#FEE2E2';
                stockBadge.style.color = '#DC2626';
              }
              if (addBtn) addBtn.disabled = true;
              if (addBtnText) addBtnText.textContent = 'Out of Stock';
            }
            
            // Update Main Image if variant has one
            if (matchedVariant.featured_image) {
              const mainImg = document.querySelector('.js-product-image');
              if (mainImg) {
                const imgUrl = matchedVariant.featured_image.src.replace('.jpg', '_1000x.jpg').replace('.png', '_1000x.png');
                mainImg.src = imgUrl;
              }
            }
          }
        });
      });
    }

    initQuantitySelectors() {
      const minus = document.querySelector('.js-qty-minus');
      const plus = document.querySelector('.js-qty-plus');
      const input = document.querySelector('.js-qty-input');
      
      if (!input) return;
      
      if (minus) minus.addEventListener('click', () => {
        let val = parseInt(input.value);
        if (val > 1) input.value = val - 1;
      });
      
      if (plus) plus.addEventListener('click', () => {
        let val = parseInt(input.value);
        input.value = val + 1;
      });
    }

    initThumbnails() {
      const thumbs = document.querySelectorAll('.js-thumbnail');
      const mainImg = document.querySelector('.js-product-image');
      
      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          thumbs.forEach(t => {
            t.style.border = 'none';
          });
          thumb.style.border = '2px solid var(--color-accent)';
          if (mainImg) mainImg.src = thumb.getAttribute('data-full-url');
        });
      });
    }

    initAjaxCart() {
      const form = document.querySelector('.js-cart-form');
      if (!form) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const submitBtn = form.querySelector('.js-add-to-cart');
        const submitText = form.querySelector('.js-add-to-cart-text');
        const submitSpinner = form.querySelector('.js-add-to-cart-spinner');
        
        // Disable button & show loading state
        if (submitBtn) submitBtn.disabled = true;
        if (submitText) submitText.style.opacity = '0';
        if (submitSpinner) submitSpinner.style.display = 'block';

        const formData = new FormData(form);
        
        const payload = {
          items: [
            {
              id: parseInt(formData.get('id')),
              quantity: parseInt(formData.get('quantity') || 1)
            }
          ]
        };

        try {
          const res = await fetch(getRoute('cart/add.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(payload)
          });
          
          if (!res.ok) {
            let errorMessage = 'Failed to add to cart';
            try {
              const errorJson = await res.json();
              if (errorJson.description) errorMessage = errorJson.description;
            } catch (e) {}
            throw new Error(errorMessage);
          }
          
          // Success
          await this.refreshCartDrawer();
          
          const drawer = document.getElementById('shopify-cart-drawer');
          const overlay = document.getElementById('shopify-drawer-overlay');
          if (drawer && overlay) {
            drawer.classList.add('is-open');
            overlay.classList.add('is-open');
            document.body.style.overflow = 'hidden';
          }
        } catch (err) {
          alert(err.message || 'Sorry, there was a problem adding this item to your cart.');
        } finally {
          // Restore button state
          if (submitBtn) submitBtn.disabled = false;
          if (submitText) submitText.style.opacity = '1';
          if (submitSpinner) submitSpinner.style.display = 'none';
        }
      });
    }
  }

  function debounce(fn, wait) {
    let t;
    return function(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  document.addEventListener('DOMContentLoaded', () => {
    window.ukDirectTheme = new UKDirectTheme();
  });

})();
